import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import AssignmentWeightExplanationModal from "@components/AssignmentWeightExplanationModal";
import ActionIconButton from "@components/buttons/ActionIconButton";
import BaseErrors from "@components/fields/BaseErrors";
import Checkbox from "@components/fields/Checkbox";
import DataSelect from "@components/fields/DataSelect";
import DirectUploadFileInput from "@components/fields/DirectUploadFileInput";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import RichTextArea from "@components/fields/RichTextArea";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import useOpener from "@hooks/useOpener";
import {
  ExpertFragment as Expert,
  ExpertCreateDocument,
  ExpertCreateMutation,
  ExpertCreateMutationVariables,
  ExpertUpdateDocument,
  ExpertUpdateMutation,
  ExpertUpdateMutationVariables,
  SpecialtyAgeEnum,
  SpecialtyConditionEnum,
  SpecialtyOtherEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { optionsForSpecialtyAge } from "@utils/optionUtils";

type FormData = {
  bio: string;
  coversAsia: boolean;
  coversEurope: boolean;
  coversUsEast: boolean;
  coversUsWest: boolean;
  email: string;
  firstName: string;
  journeyAssignmentWeight: number;
  lastName: string;
  photoBlobId: string;
  specialtyAges: SpecialtyAgeEnum[];
  specialtyConditions: SpecialtyConditionEnum[];
  specialtyOthers: SpecialtyOtherEnum[];
  timezone: string;
  zoomId: string;
};

type Props = {
  autofocus: boolean;
  expert?: Expert;
  onCancel: () => void;
  onError: () => void;
  onSuccess: (expert: Expert | undefined) => void;
};

const ExpertForm = ({ autofocus, expert, onCancel, onError, onSuccess }: Props) => {
  const defaultValues: FormData = {
    bio: expert?.bio ?? "",
    coversAsia: expert?.coversAsia ?? false,
    coversEurope: expert?.coversEurope ?? false,
    coversUsEast: expert?.coversUsEast ?? false,
    coversUsWest: expert?.coversUsWest ?? false,
    email: expert?.email?.email ?? "",
    firstName: expert?.firstName ?? "",
    journeyAssignmentWeight: expert?.journeyAssignmentWeight ?? 0.0,
    lastName: expert?.lastName ?? "",
    photoBlobId: "",
    specialtyAges: expert?.specialtyAges ?? [],
    specialtyConditions: expert?.specialtyConditions ?? [],
    specialtyOthers: expert?.specialtyOthers ?? [],
    timezone: expert?.timezone ?? "",
    zoomId: expert?.zoomId ?? ""
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const assignmentWeightExplanationOpener = useOpener();

  const [fileInputBusy, setFileInputBusy] = useState<boolean>(false);

  const [createExpert, { client }] = useMutation<ExpertCreateMutation, ExpertCreateMutationVariables>(
    ExpertCreateDocument,
    {
      update: () => {
        client.resetStore();
      }
    }
  );
  const [updateExpert] = useMutation<ExpertUpdateMutation, ExpertUpdateMutationVariables>(ExpertUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      bio: yup.string().required(intl.formatMessage({ id: "errors.expert.bio.blank" })),
      coversAsia: yup.boolean().required(intl.formatMessage({ id: "errors.expert.coversAsia.blank" })),
      coversEurope: yup.boolean().required(intl.formatMessage({ id: "errors.expert.coversEurope.blank" })),
      coversUsEast: yup.boolean().required(intl.formatMessage({ id: "errors.expert.coversUsEast.blank" })),
      coversUsWest: yup.boolean().required(intl.formatMessage({ id: "errors.expert.coversUsWest.blank" })),
      email: expert?.id
        ? yup.string().defined()
        : yup
            .string()
            .email()
            .defined()
            .required(intl.formatMessage({ id: "errors.expert.email.blank" })),
      firstName: yup.string().required(intl.formatMessage({ id: "errors.expert.firstName.blank" })),
      journeyAssignmentWeight: yup
        .number()
        .defined()
        .typeError(intl.formatMessage({ id: "errors.expert.journeyAssignmentWeight.number" }))
        .min(0.0, intl.formatMessage({ id: "errors.expert.journeyAssignmentWeight.tooSmall" }))
        .max(100.0, intl.formatMessage({ id: "errors.expert.journeyAssignmentWeight.tooBig" })),
      lastName: yup.string().required(intl.formatMessage({ id: "errors.expert.lastName.blank" })),
      photoBlobId: expert?.id
        ? yup.string().defined()
        : yup.string().required(intl.formatMessage({ id: "errors.expert.photoBlobId.blank" })),
      specialtyAges: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(SpecialtyAgeEnum)).defined()),
      specialtyConditions: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(SpecialtyConditionEnum)).defined()),
      specialtyOthers: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(SpecialtyOtherEnum)).defined()),
      timezone: yup.string().required(intl.formatMessage({ id: "errors.expert.timezone.blank" })),
      zoomId: yup.string().required(intl.formatMessage({ id: "errors.expert.zoomId.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const specialtyAges = form.watch("specialtyAges");
  const specialtyConditions = form.watch("specialtyConditions");
  const specialtyOthers = form.watch("specialtyOthers");

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (specialtyAges.length + specialtyConditions.length + specialtyOthers.length > 10) {
      form.setError("specialtyOthers", {
        message: intl.formatMessage({ id: "errors.expert.otherSpecialties.tooMany" }),
        type: "validate"
      });
      onError();
      return;
    }

    if (expert?.id) {
      return updateExpert({
        variables: {
          ...form.getValues(),
          id: expert.id,
          journeyAssignmentWeight: parseFloat(form.getValues().journeyAssignmentWeight.toString())
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.expertUpdate,
          intl,
          onError,
          onSuccess: () => {
            if (response.data?.expertUpdate?.expert) {
              onSuccess(response.data.expertUpdate.expert);
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.expert"
        });
      });
    }
    return createExpert({
      variables: {
        ...form.getValues()
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.expertCreate,
        intl,
        onError,
        onSuccess: () => {
          if (response.data?.expertCreate?.expert) {
            onSuccess(response.data?.expertCreate.expert);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.expert"
      });
    });
  }, [
    createExpert,
    expert?.id,
    form,
    intl,
    onError,
    onSuccess,
    specialtyAges.length,
    specialtyConditions.length,
    specialtyOthers.length,
    updateExpert
  ]);

  const setPhotoFileTypeError = useCallback(() => {
    form.setError("photoBlobId", {
      message: intl.formatMessage({ id: "errors.expert.photoBlobId.invalid" }),
      type: "validate"
    });
  }, [form, intl]);

  const clearPhotoFileTypeError = useCallback(() => {
    form.clearErrors("photoBlobId");
  }, [form]);

  const ageSpecialtyOptions = optionsForSpecialtyAge(intl);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className="ion-margin-bottom bold">
        <FormattedMessage id="forms.expert.notHidden" />
      </div>
      <Input
        autofocus={autofocus}
        error={form.formState.errors.firstName}
        label={intl.formatMessage({ id: "forms.expert.firstNameLabel" })}
        register={form.register("firstName")}
        required
      />
      <Input
        error={form.formState.errors.lastName}
        label={intl.formatMessage({ id: "forms.expert.lastNameLabel" })}
        register={form.register("lastName")}
        required
      />
      <RichTextArea<FormData>
        control={form.control}
        error={form.formState.errors.bio}
        journeyId={null}
        label={intl.formatMessage({ id: "forms.expert.bioLabel" })}
        name="bio"
        required
      />
      <DirectUploadFileInput<FormData, string>
        clearFileTypeError={clearPhotoFileTypeError}
        control={form.control}
        errors={form.formState.errors.photoBlobId ? [form.formState.errors.photoBlobId] : []}
        label={intl.formatMessage({ id: "forms.expert.photoBlobIdLabel" })}
        name="photoBlobId"
        onBusyChange={setFileInputBusy}
        required
        setFileTypeError={setPhotoFileTypeError}
        setValue={value => {
          form.setValue("photoBlobId", value);
        }}
        validTypes={["image/jpg", "image/jpeg", "image/gif", "image/png", "image/tiff"]}
      />
      <div className="ion-margin-top-more bold">
        <FormattedMessage id="forms.expert.hidden" />
      </div>
      {(!expert || !expert.id) && (
        <Input
          error={form.formState.errors.email}
          label={intl.formatMessage({ id: "forms.expert.emailLabel" })}
          register={form.register("email")}
          required
        />
      )}

      <Select<FormData>
        control={form.control}
        error={form.formState.errors.specialtyAges}
        isMulti
        label={intl.formatMessage({ id: "forms.expert.ageSpecialtiesLabel" })}
        name="specialtyAges"
        options={ageSpecialtyOptions}
      />
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol size="auto">
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.help"
              icon="questionCircle"
              onClick={assignmentWeightExplanationOpener.open}
              size="small"
            />
          </IonCol>
          <IonCol>
            <Input
              error={form.formState.errors.journeyAssignmentWeight}
              label={intl.formatMessage({ id: "forms.expert.journeyAssignmentWeightLabel" })}
              register={form.register("journeyAssignmentWeight")}
              required
              step="1.0"
              type="number"
            />
          </IonCol>
        </IonRow>
      </IonGrid>
      <Input
        error={form.formState.errors.zoomId}
        label={intl.formatMessage({ id: "forms.expert.zoomIdLabel" })}
        register={form.register("zoomId")}
        required
      />
      <DataSelect
        control={form.control}
        error={form.formState.errors.timezone}
        label={intl.formatMessage({ id: "forms.expert.timezoneLabel" })}
        name="timezone"
        required
        type="timezones"
      />
      <div className="ion-margin-top">
        <Checkbox<FormData> control={form.control} error={form.formState.errors.coversAsia} name="coversAsia">
          <FormattedMessage id="forms.expert.coversAsiaLabel" />
        </Checkbox>
      </div>
      <div className="ion-margin-top">
        <Checkbox<FormData> control={form.control} error={form.formState.errors.coversEurope} name="coversEurope">
          <FormattedMessage id="forms.expert.coversEuropeLabel" />
        </Checkbox>
      </div>
      <div className="ion-margin-top">
        <Checkbox<FormData> control={form.control} error={form.formState.errors.coversUsEast} name="coversUsEast">
          <FormattedMessage id="forms.expert.coversUsEastLabel" />
        </Checkbox>
      </div>
      <div className="ion-margin-top">
        <Checkbox<FormData> control={form.control} error={form.formState.errors.coversUsWest} name="coversUsWest">
          <FormattedMessage id="forms.expert.coversUsWestLabel" />
        </Checkbox>
      </div>
      <FormButtons hookForm={form} onCancel={onCancel} saveDisabled={fileInputBusy} />
      <AssignmentWeightExplanationModal opener={assignmentWeightExplanationOpener} />
    </Form>
  );
};

export default ExpertForm;
